import { passwordSchema } from '@hr/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FieldError } from '@/components/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { uk } from '@/i18n/uk';
import { acceptInvite, fetchInvite } from '@/lib/auth';

export const Route = createFileRoute('/invite/$token')({
  loader: ({ params }) => fetchInvite(params.token),
  component: InvitePage,
});

const schema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: uk.invite.mismatch });

function InvitePage() {
  const t = uk.invite;
  const invite = Route.useLoaderData();
  const { token } = Route.useParams();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });

  const onSubmit = form.handleSubmit(async ({ password }) => {
    setError(null);
    try {
      await acceptInvite({ token, password });
      await router.navigate({ to: '/' });
    } catch (e) {
      setError(e instanceof Error ? e.message : uk.common.error);
    }
  });

  return (
    <main className="flex min-h-svh items-center justify-center bg-muted/40 p-4">
      <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
        <div className="mb-6 space-y-1 text-center">
          <img src="/favicon.svg" alt="" className="mx-auto mb-3 size-10" />
          <h1 className="text-xl font-semibold">{invite ? t.title : t.invalid}</h1>
          <p className="text-sm text-muted-foreground">
            {invite ? `${invite.fullName} · ${invite.email}` : t.invalidHint}
          </p>
        </div>
        {invite ? (
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <p className="text-sm">{t.subtitle}</p>
            {/* Прихований логін — щоб менеджер паролів зберіг пару email + пароль. */}
            <input type="email" autoComplete="username" value={invite.email} readOnly hidden />
            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                autoFocus
                aria-invalid={Boolean(form.formState.errors.password)}
                {...form.register('password')}
              />
              <FieldError error={form.formState.errors.password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t.confirm}</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                aria-invalid={Boolean(form.formState.errors.confirm)}
                {...form.register('confirm')}
              />
              <FieldError error={form.formState.errors.confirm} />
            </div>
            {error && (
              <p
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t.submitting : t.submit}
            </Button>
          </form>
        ) : (
          <Button asChild className="w-full">
            <Link to="/login">{t.toLogin}</Link>
          </Button>
        )}
      </div>
    </main>
  );
}
